'use strict';

const {
  canonicalizeMarketInputs,
  buildCanonicalMarketKey,
} = require('./marketCanonicalization');

const MARKET_DATA_VALIDATION_ERROR = 'MARKET_DATA_VALIDATION_FAILED';

/** Canonical role field and accepted aliases (flat employee record). */
const ROLE_FIELD_ALIASES = Object.freeze([
  'role',
  'title',
  'jobTitle',
  'job_title',
  'position',
  'jobRole',
  'job_role',
]);

/** Canonical workLocation field and accepted aliases. */
const WORK_LOCATION_FIELD_ALIASES = Object.freeze([
  'workLocation',
  'work_location',
  'location',
  'city',
  'officeLocation',
  'office_location',
  'jobLocation',
  'job_location',
  'workCity',
  'work_city',
  'metro',
  'state',
]);

function normalizeKey(key) {
  return String(key || '')
    .trim()
    .replace(/^\uFEFF/, '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');
}

function normalizeMarketString(value) {
  if (value == null || value === '') return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return trimmed.replace(/\s+/g, ' ');
}

function flattenEmployeeFields(employee, depth = 0) {
  const out = {};
  if (!employee || typeof employee !== 'object' || Array.isArray(employee)) return out;
  if (depth > 2) return out;

  for (const [key, value] of Object.entries(employee)) {
    if (value == null || value === '') continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      if (depth === 0 && (key === 'resumeProfile' || key === 'profile' || key === 'address')) {
        Object.assign(out, flattenEmployeeFields(value, depth + 1));
      }
      continue;
    }
    const nk = normalizeKey(key);
    if (nk) out[nk] = value;
    out[key] = value;
  }
  return out;
}

function resolveFieldFromEmployee(employee, aliases) {
  if (!employee || typeof employee !== 'object') return '';
  const flat = flattenEmployeeFields(employee);

  for (const alias of aliases) {
    const direct = employee[alias];
    const normalizedDirect = normalizeMarketString(direct);
    if (normalizedDirect) return normalizedDirect;

    const flatValue = flat[normalizeKey(alias)];
    const normalizedFlat = normalizeMarketString(flatValue);
    if (normalizedFlat) return normalizedFlat;
  }

  return '';
}

/**
 * Single source of truth for Adzuna market lookup inputs.
 * @param {object} employee
 * @returns {{
 *   isValid: boolean,
 *   missingFields: string[],
 *   normalizedRole: string,
 *   normalizedWorkLocation: string,
 * }}
 */
function validateMarketDataInputs(employee) {
  const normalizedRole = resolveFieldFromEmployee(employee, ROLE_FIELD_ALIASES);
  const normalizedWorkLocation = resolveFieldFromEmployee(employee, WORK_LOCATION_FIELD_ALIASES);
  const missingFields = [];
  if (!normalizedRole) missingFields.push('role');
  if (!normalizedWorkLocation) missingFields.push('workLocation');

  const explicitLevel = resolveFieldFromEmployee(employee, ['jobLevel', 'job_level', 'level']);
  const industry = normalizeMarketString(employee?.industry);
  const canonical = canonicalizeMarketInputs({
    role: normalizedRole,
    workLocation: normalizedWorkLocation,
    jobLevel: explicitLevel,
    industry,
  });

  return {
    isValid: missingFields.length === 0,
    missingFields,
    normalizedRole: canonical.searchRole || normalizedRole,
    normalizedWorkLocation: canonical.searchLocation || normalizedWorkLocation,
    canonicalRole: canonical.canonicalRole,
    canonicalWorkLocation: canonical.canonicalCity,
    seniorityBand: canonical.seniorityBand,
    marketCacheKey: canonical.cacheKey,
    rawRole: normalizedRole,
    rawWorkLocation: normalizedWorkLocation,
  };
}

/**
 * Stable cache key for a validated employee market lookup.
 * @param {object} employee
 */
function marketCacheKeyForEmployee(employee) {
  const validation = validateMarketDataInputs(employee);
  if (!validation.isValid) return '';
  if (validation.marketCacheKey) return validation.marketCacheKey;
  return buildCanonicalMarketKey({
    role: validation.rawRole || validation.normalizedRole,
    workLocation: validation.rawWorkLocation || validation.normalizedWorkLocation,
    jobLevel: String(employee?.jobLevel || employee?.level || '').trim(),
    industry: String(employee?.industry || '').trim(),
  });
}

/**
 * Structured validation error for API and UI consumers.
 * @param {string[]} missingFields
 */
function buildMarketDataValidationError(missingFields = []) {
  const fields = Array.isArray(missingFields) ? missingFields : [];
  return {
    errorCode: MARKET_DATA_VALIDATION_ERROR,
    code: MARKET_DATA_VALIDATION_ERROR,
    message: 'Market data inputs are invalid',
    missingFields: fields,
    suggestion:
      fields.length === 1
        ? fields[0] === 'role'
          ? 'Provide role in employee profile'
          : 'Provide work location in employee profile'
        : 'Provide role and work location in employee profile',
  };
}

/**
 * User-facing copy for profile market snapshot (deterministic, field-specific).
 * @param {string[]} missingFields
 */
function marketDataValidationUserMessage(missingFields = []) {
  const fields = Array.isArray(missingFields) ? missingFields : [];
  if (fields.length === 1 && fields[0] === 'role') {
    return 'Add a role to see market data for this employee.';
  }
  if (fields.length === 1 && fields[0] === 'workLocation') {
    return 'Add a work location to see market data for this employee.';
  }
  return 'Add a role and work location to see market data for this employee.';
}

/**
 * Apply canonical role/workLocation onto a shallow employee copy for downstream use.
 * @param {object} employee
 */
function withCanonicalMarketFields(employee) {
  const validation = validateMarketDataInputs(employee);
  if (!employee || typeof employee !== 'object') {
    return {
      role: validation.normalizedRole,
      workLocation: validation.normalizedWorkLocation,
      marketCacheKey: validation.marketCacheKey,
    };
  }
  return {
    ...employee,
    role: validation.normalizedRole || employee.role,
    workLocation: validation.normalizedWorkLocation || employee.workLocation,
    marketCacheKey: validation.marketCacheKey,
  };
}

/**
 * Log when two validation layers disagree (guards against drift).
 * @param {string} context
 * @param {object} employee
 * @param {{ isValid: boolean, missingFields?: string[] }} primary
 * @param {{ isValid: boolean, missingFields?: string[] }} secondary
 */
function logMarketValidationMismatch(context, employee, primary, secondary) {
  if (!primary || !secondary || primary.isValid === secondary.isValid) return;
  const employeeId = employee?.id ?? employee?.externalEmployeeId ?? null;
  console.warn('[marketValidation] layer mismatch', {
    context,
    employeeId,
    primaryMissing: primary.missingFields || [],
    secondaryMissing: secondary.missingFields || [],
  });
}

function isAdzunaSource(source) {
  const s = String(source || '').toLowerCase();
  return s === 'adzuna' || s === 'adzuna_cached' || s.startsWith('adzuna');
}

/** Employee has enough context for an Adzuna search. */
function hasRoleAndLocation(employee) {
  return validateMarketDataInputs(employee).isValid;
}

/** Adzuna path is usable when we have positive median comp. */
function isUsableAdzunaProviderData(provider) {
  if (!provider) return false;
  const src = String(provider.source || '').toLowerCase();
  if (!src.startsWith('adzuna')) return false;
  const mb = Number(provider.medianBaseComp);
  const mt = Number(provider.medianTotalComp);
  return Number.isFinite(mb) && mb > 0 && Number.isFinite(mt) && mt > 0;
}

/** Provider payload from Adzuna is complete enough to build a benchmark. */
function validateProviderPayload(provider) {
  if (!isUsableAdzunaProviderData(provider)) return false;
  const openings = Number(provider.openingsCount);
  const pressure = Number(provider.demandPressureScore);
  const median = Number(provider.medianTotalComp);
  if (!Number.isFinite(median) || median <= 0) return false;
  if (openings === 0 && pressure === 0) return false;
  return true;
}

/** Persisted benchmark is renderable Adzuna market data. */
function isValidBenchmark(benchmark) {
  if (!benchmark || typeof benchmark !== 'object') return false;
  if (benchmark.status !== 'available') return false;
  if (!isAdzunaSource(benchmark.source)) return false;
  const median = Number(benchmark.marketSnapshot?.medianCompensation?.totalAnnual);
  const openings = Number(benchmark.demandSignals?.localOpenings?.estimate);
  const hasMedian = Number.isFinite(median) && median > 0;
  const hasOpenings = Number.isFinite(openings) && openings > 0;
  return hasMedian || hasOpenings;
}

function coerceFinite(x) {
  if (x == null || x === '') return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/** Canonical positive market benchmark on the employee row. */
function hasCanonicalMarketSalary(employee) {
  const mkt = coerceFinite(
    employee?.marketSalary ??
      employee?.marketMid ??
      employee?.compBenchmark ??
      employee?.benchmarkSalary
  );
  if (mkt != null && mkt > 0) return true;
  const snap = employee?.marketInsightsSnapshot;
  const fromSnap = coerceFinite(snap?.benchmarkAnnual ?? snap?.medianTotalComp);
  return fromSnap != null && fromSnap > 0;
}

/**
 * Market data is present enough for Adzuna-backed scoring.
 * Canonical marketSalary alone is NOT sufficient — requires validated Adzuna benchmark.
 */
function isMarketDataReady(employee) {
  return isValidBenchmark(employee?.marketComparison);
}

/**
 * Market fetch may still be in progress (role+location present, no benchmark yet).
 */
function isMarketPending(employee) {
  if (isMarketDataReady(employee)) return false;
  return hasRoleAndLocation(employee);
}

const UNVERIFIED_MARKET_SALARY_KEYS = Object.freeze([
  'marketSalary',
  'marketMid',
  'compBenchmark',
  'benchmarkSalary',
  'market_mid',
  'market_salary',
]);

/**
 * Verified market salary from Adzuna benchmark only.
 * @param {object|null} employee
 * @returns {number|null}
 */
function verifiedMarketSalary(employee) {
  if (!isValidBenchmark(employee?.marketComparison)) return null;
  const base = coerceFinite(employee.marketComparison?.marketSnapshot?.medianCompensation?.baseAnnual);
  const total = coerceFinite(employee.marketComparison?.marketSnapshot?.medianCompensation?.totalAnnual);
  return base != null && base > 0 ? base : total != null && total > 0 ? total : null;
}

/**
 * Remove unverified market salary aliases and stale snapshots.
 * Does not mutate the input object.
 * @param {object|null} employee
 * @returns {object|null}
 */
function sanitizeEmployeeMarketRecord(employee) {
  if (!employee || typeof employee !== 'object') return employee;

  if (isValidBenchmark(employee.marketComparison)) {
    const verified = verifiedMarketSalary(employee);
    if (verified != null && employee.marketSalary !== verified) {
      return { ...employee, marketSalary: verified };
    }
    return employee;
  }

  let changed = false;
  const sanitized = { ...employee };

  for (const key of UNVERIFIED_MARKET_SALARY_KEYS) {
    if (sanitized[key] != null) {
      delete sanitized[key];
      changed = true;
    }
  }

  if (sanitized.marketInsightsSnapshot) {
    delete sanitized.marketInsightsSnapshot;
    changed = true;
  }

  if (sanitized.marketComparison && !isValidBenchmark(sanitized.marketComparison)) {
    delete sanitized.marketComparison;
    delete sanitized.marketComparisonFetchedAt;
    delete sanitized.marketCacheKey;
    changed = true;
  }

  return changed ? sanitized : employee;
}

module.exports = {
  MARKET_DATA_VALIDATION_ERROR,
  ROLE_FIELD_ALIASES,
  WORK_LOCATION_FIELD_ALIASES,
  validateMarketDataInputs,
  marketCacheKeyForEmployee,
  buildMarketDataValidationError,
  marketDataValidationUserMessage,
  withCanonicalMarketFields,
  logMarketValidationMismatch,
  isAdzunaSource,
  hasRoleAndLocation,
  validateProviderPayload,
  isValidBenchmark,
  isUsableAdzunaProviderData,
  hasCanonicalMarketSalary,
  isMarketDataReady,
  isMarketPending,
  verifiedMarketSalary,
  sanitizeEmployeeMarketRecord,
  UNVERIFIED_MARKET_SALARY_KEYS,
};
